import React, { useId } from "react";
import { cn } from "../../lib/cn";

/**
 * Textarea
 * @param {string} [label]
 * @param {string} [placeholder]
 * @param {string} [error] - error message; shows red border + message when set
 * @param {string} [helperText]
 * @param {boolean} [disabled]
 * @param {number} [rows=4]
 * @param {string} [className]
 * @param {string} [id]
 */
export const Textarea = React.forwardRef(function Textarea(
  {
    label,
    placeholder,
    error,
    helperText,
    disabled = false,
    rows = 4,
    className,
    id,
    ...props
  },
  ref
) {
  const generatedId = useId();
  const inputId = id || generatedId;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="mb-1.5 block text-sm font-medium text-neutral-900 dark:text-neutral-100"
        >
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : helperText ? helperId : undefined}
        className={cn(
          "w-full resize-y rounded border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 transition-colors",
          "outline-none focus:border-primary-300 focus-visible:ring-2 focus-visible:ring-primary-200 dark:focus-visible:ring-primary-500/40 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-neutral-900",
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-neutral-50 dark:disabled:bg-neutral-800",
          error && "border-danger-500 focus:border-danger-500 focus-visible:ring-danger-500",
          className
        )}
        {...props}
      />
      {error ? (
        <p id={errorId} className="mt-1.5 text-sm text-danger-700 dark:text-danger-500">
          {error}
        </p>
      ) : helperText ? (
        <p id={helperId} className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
          {helperText}
        </p>
      ) : null}
    </div>
  );
});